import InfoCalloutToneEnum from '../../../Enums/InfoCalloutToneEnum';
import InputTypesEnum from '../../../Enums/InputTypesEnum';
import PlanningDetailsItemService from '../../../Services/PlanningDetailsItemService';
import mathHelper from '../../../Helpers/mathHelper';
import PlanningItemMultiEditItem from '../../../Domain/PlanningItemMultiEditItem';

export default class PlanningItemMultiEditWindowDescription {
  static titleForCreate = 'Edit Multiple Planning Items';
  static titleForEdit = 'Edit Multiple Planning Items';
  static itemType = PlanningItemMultiEditItem;
  static messageOnSuccess = 'Update is ready.';
  // The fieldwork-period grid and the per-country planning sheet (min-width 1000px)
  // don't fit the 880px compact centered modal — force the full-size window.
  static compact = false;

  static approveBeforeSaveCheck = () => true;
  static approveBeforeSaveMessage = (item) =>
    `You are going to update planned shop counts${
      item.Periods.some((period) => period.apply) ? ' and fieldwork start and end dates' : ''
    }.`;

  static serviceFunction = async (item) =>
    PlanningDetailsItemService.EditMultiplePlanningDetailItemsByPricingIds(
      item.PricingIds,
      item.Periods.map((period) => period.apply),
      item.Periods.map((period) => period.startDate),
      item.Periods.map((period) => period.endDate),
      item.CountryPlans.map((row) => ({
        pricingId: row.pricingId,
        numberOfPlannedAssignments: row.numberOfPlannedAssignments,
        applyTargetRate: row.applyTargetRate,
        targetAssignmentRate: row.targetAssignmentRate,
        targetCompletionRate: row.targetCompletionRate,
      }))
    );

  static pagesInGeneralEditWizzard = [
    {
      fields: [
        {
          fieldName: 'Info',
          label: '',
          className: 'col-12',
          type: InputTypesEnum.InfoCallout,
          // One line per part of the form, each saying what it covers and which control
          // moves that figure. It used to be a single sentence ("target rates apply to the
          // 0 checked countries…") that read like an error report the moment a count was
          // zero, and gave no clue which tick box produced the number. The badge colours
          // are the controls' own — navy for the Apply target rates checkboxes, green for
          // the periods' Apply switches — so the note works as a legend for the form below.
          buildContent: (item) => {
            const countryPlans = item.CountryPlans ?? [];
            const periods = item.Periods ?? [];
            const checked = countryPlans.filter((row) => row.applyTargetRate).length;
            const applied = periods.filter((period) => period.apply).length;
            const countryWord = countryPlans.length === 1 ? 'country' : 'countries';
            return {
              paragraphs: ["Planning Details in Status **'Cancelled'** and **'Closed'** will not be updated."],
              facts: [
                {
                  tone: InfoCalloutToneEnum.Neutral,
                  value: `all ${countryPlans.length}`,
                  label: 'Planned shops',
                  detail: `every ${countryWord} below gets the count you type for it. Nothing to tick.`,
                },
                {
                  tone: InfoCalloutToneEnum.Navy,
                  value: `${checked} of ${countryPlans.length}`,
                  muted: checked === 0,
                  label: 'Target rates',
                  detail:
                    checked === 0
                      ? 'not in scope yet — tick **Apply target rates** on a country below to include it.'
                      : `${checked === 1 ? 'the country' : 'the countries'} you ticked **Apply target rates** for.`,
                },
                {
                  tone: InfoCalloutToneEnum.Green,
                  value: `${applied} of ${periods.length}`,
                  muted: applied === 0,
                  label: 'Fieldwork dates',
                  detail:
                    applied === 0
                      ? 'not in scope yet — switch **Apply** on for a period below to include it.'
                      : `${applied === 1 ? 'the period' : 'the periods'} you switched **Apply** on for.`,
                },
              ],
              chips: countryPlans.map((row) => `${row.country} · ${row.pricingId}`),
            };
          },
        },
        {
          fieldName: 'TimeSpan',
          label: 'Frequency',
          className: 'col-6',
          type: InputTypesEnum.PillSelect,
          selectvalues: [
            { value: 'weekly', label: 'Weekly' },
            { value: 'monthly', label: 'Monthly' },
            { value: 'bimonthly', label: 'BiMonthly' },
            { value: 'quarterly', label: 'Quarterly' },
            { value: 'yearly', label: 'Yearly' },
            { value: 'other', label: 'Other' },
          ],
        },
        {
          fieldName: 'Periods',
          label: 'Fieldwork Periods',
          className: 'col-12',
          type: InputTypesEnum.FieldworkPeriodDateTable,
          validityChecker: (value) => {
            const applied = (value ?? []).filter((period) => period.apply);
            if (applied.some((period) => !period.startDate || !period.endDate)) {
              return 'Every applied period needs a start and an end date.';
            }
            const toNoonStamp = (date) => new Date(date).setHours(12, 0, 0, 0);
            if (applied.some((period) => toNoonStamp(period.endDate) <= toNoonStamp(period.startDate))) {
              return 'The end date of every applied period must be after its start date.';
            }
            return '';
          },
        },
        {
          fieldName: 'CountryPlans',
          label: 'Planned Shop Counts and Target Rates',
          className: 'col-12',
          type: InputTypesEnum.CountryPlanningTable,
          validityChecker: (value) => {
            if (!value) return '';
            if (value.some((row) => !(parseInt(row.numberOfPlannedAssignments, 10) > 0))) {
              return 'Every planned shop count must be greater than 0.';
            }
            if (
              value.some(
                (row) =>
                  row.applyTargetRate &&
                  (mathHelper.Sum(row.targetAssignmentRate) !== 100 || mathHelper.Sum(row.targetCompletionRate) !== 100)
              )
            ) {
              return 'Applied target rates must add up to 100%.';
            }
            return '';
          },
        },
      ],
    },
  ];
}
